import { push as pushLocation } from "react-router-redux";
import { retrieveProfile } from "../profile/profileActions";
import { credFrontToBack, frontToBack, passwordChangeFrontToBack, passwordForgotFrontToBack, passwordResetFrontToBack } from "./dataConversion";
import { fieldErrorsBackToFront } from "./../misc/dataConversion";
import AuthRepository from "./AuthRepository";
import { resetMessages } from "../misc/miscActions";
import { openModal, closeModal } from "../ui/uiActions";
import { apiService } from "assets/scripts/domain/apiService";
import moment from "moment";
import { RegistrationChannel } from "../validation/validationModels";
export const AUTH_ACTIONS = {
    /* --- login/logut --- */
    LOGIN_STARTED: "login-started",
    LOGIN_SUCCEEDED_FALSE: "login-succeeded-false",
    LOGIN_SUCCEEDED_TRUE: "login-succeeded-true",
    LOGIN_FAILED: "login-failed",
    LOGOUT: "logout",
    /* --- Password creation --- */
    CREATE_STARTED: "create-started",
    CREATE_SUCCEEDED_FALSE: "create-succeeded-false",
    CREATE_SUCCEEDED_TRUE: "create-succeeded-true",
    CREATE_FAILED: "create-failed",
    CREATE_PASSWORD_FAILED: "create-password-failed",
    /* --- Change Password --- */
    CHANGE_STARTED: "change-password-started",
    CHANGE_SUCCEEDED_FALSE: "change-password-succeeded-false",
    CHANGE_SUCCEEDED_TRUE: "change-password-succeeded-true",
    CHANGE_FAILED: "change-password-failed",
    /* --- Forgot Password (to send email with link) --- */
    FORGOT_STARTED: "forgot-password-started",
    FORGOT_SUCCEEDED_TRUE: "forgot-password-succeeded-true",
    FORGOT_SUCCEEDED_FALSE: "forgot-password-succeeded-false",
    FORGOT_FAILED: "forgot-password-failed",
    /* --- Reset Password (after link in email has been clicked) --- */
    RESET_STARTED: "reset-password-started",
    RESET_SUCCEEDED_TRUE: "reset-password-succeeded-true",
    RESET_SUCCEEDED_FALSE: "reset-password-succeeded-false",
    RESET_FAILED: "reset-password-failed",
    /* --- security questions --- */
    QUESTION_FETCH_STARTED: "question-fetch-started",
    QUESTION_FETCH_SUCCEEDED: "question-fetch-succeeded",
    QUESTION_FETCH_FAILED: "question-fetch-failed",
    CURRENT_QUESTION_FETCH_STARTED: "current-question-fetch-started",
    CURRENT_QUESTION_FETCH_SUCCEEDED: "current-question-fetch-succeeded",
    CURRENT_QUESTION_FETCH_FAILED: "current-question-fetch-failed",
    /* --- session timeout --- */
    RETRIEVE_SESSION_LENGTH_SUCCEEDED: "retrieve-session-length-succeeded",
    RETRIEVE_SESSION_LENGTH_FAILED: "retrieve-session-length-failed",
    NEW_SESSION_FAILED: "new-session-failed",
    SESSION_TIMEOUT_STATUS: "session-timeout-status",
    API_REQUEST: "auth-api-request",
    SESSION_WARNING: "auth-session-warning"
};
/*
 * Dispatches series of status actions while using AuthRepository to login
 * @method l
 * @param {Object} credentials User's login credentials
 * @param {String} recaptcha Captcha response
 * @returns {Function} Redux thunk
 */
export const login = (credentials, recaptcha) => async (dispatch) => {
    dispatch({ type: AUTH_ACTIONS.LOGIN_STARTED });
    const mappedCredentials = credFrontToBack(credentials, recaptcha);
    let response;
    try {
        response = await AuthRepository.login(mappedCredentials);
    }
    catch (error) {
        return dispatch({ type: AUTH_ACTIONS.LOGIN_FAILED, payload: error });
    }
    if (!response.success) {
        const preparedErrors = fieldErrorsBackToFront(response.errors);
        dispatch({ type: AUTH_ACTIONS.LOGIN_SUCCEEDED_FALSE, payload: preparedErrors });
    }
    else {
        try {
            const newSessionResponse = await AuthRepository.setupSession();
            if (!newSessionResponse.success) {
                throw new Error("Unexpected result");
            }
            apiService.updateXsrfToken(newSessionResponse.data);
        }
        catch (error) {
            return dispatch({
                type: AUTH_ACTIONS.NEW_SESSION_FAILED,
                payload: error
            });
        }
        let metaResponse;
        try {
            metaResponse = await AuthRepository.fetchSessionLength();
            if (!metaResponse.success) {
                throw new Error("Unexpected result");
            }
        }
        catch (error) {
            return dispatch({
                type: AUTH_ACTIONS.RETRIEVE_SESSION_LENGTH_FAILED,
                payload: error
            });
        }
        dispatch({
            type: AUTH_ACTIONS.RETRIEVE_SESSION_LENGTH_SUCCEEDED,
            payload: metaResponse.data.session
        });
        const { error } = await dispatch(retrieveProfile());
        if (error) {
            dispatch({ type: AUTH_ACTIONS.LOGIN_FAILED, payload: error });
            return;
        }
        dispatch({
            type: AUTH_ACTIONS.LOGIN_SUCCEEDED_TRUE,
            payload: {
                userId: response.data.userId,
                lastLogin: response.data.lastLogin
            }
        });
        dispatch(resetMessages("contact"));
        dispatch(pushLocation("/secured"));
    }
};
/**
 * Dispatches series of status actions while using AuthRepository to create a user account
 *
 * @method create
 * @param {Object} pwData Form data from new account form
 * @param {Object} validationRequest Information used to create the account
 * @returns {Function} Redux thunk
 */
export const create = (pwData, validationRequest) => async (dispatch, getState) => {
    const state = getState();
    dispatch({ type: AUTH_ACTIONS.CREATE_STARTED });
    const creationRequest = {
        ...validationRequest,
        ...frontToBack(pwData),
        channel: state.validation.registrationChannel ?? RegistrationChannel.Unknown
    };
    let response;
    try {
        response = await AuthRepository.create(creationRequest);
    }
    catch (error) {
        return dispatch({ type: AUTH_ACTIONS.CREATE_FAILED, payload: error });
    }
    if (!response.success) {
        const preparedErrors = fieldErrorsBackToFront(response.errors);
        dispatch({ type: AUTH_ACTIONS.CREATE_SUCCEEDED_FALSE, payload: preparedErrors });
    }
    else {
        try {
            const newSessionResponse = await AuthRepository.setupSession();
            if (!newSessionResponse.success) {
                throw new Error("Unexpected result");
            }
            apiService.updateXsrfToken(newSessionResponse.data);
        }
        catch (error) {
            dispatch({ type: AUTH_ACTIONS.LOGOUT, payload: false });
            dispatch(pushLocation("/public"));
        }
        const { error } = await dispatch(retrieveProfile());
        if (error) {
            dispatch({ type: AUTH_ACTIONS.LOGOUT, payload: false });
            dispatch(pushLocation("/public"));
        }
        else {
            dispatch({
                type: AUTH_ACTIONS.CREATE_SUCCEEDED_TRUE,
                payload: response.data.userId
            });
            dispatch(pushLocation("/secured/createProfile"));
        }
    }
};
/**
 * Dispatches series of status actions while using AuthRepository to fetch security questions for the account
 *
 * @method fetchQuestions
 * @returns {Function} Redux thunk
 */
export const fetchQuestions = () => async (dispatch) => {
    dispatch({ type: AUTH_ACTIONS.QUESTION_FETCH_STARTED });
    let response;
    try {
        response = await AuthRepository.fetchSecurityQuestions();
        if (!response.success) {
            throw new Error("Unexpected result");
        }
    }
    catch (error) {
        return dispatch({ type: AUTH_ACTIONS.QUESTION_FETCH_FAILED, payload: error });
    }
    dispatch({
        type: AUTH_ACTIONS.QUESTION_FETCH_SUCCEEDED,
        payload: response.data
    });
};
/**
 * Dispatches series of status actions while using AuthRepository to fetch the security question for the current user
 */
export const fetchCurrentQuestion = (userId, token) => async (dispatch) => {
    dispatch({ type: AUTH_ACTIONS.CURRENT_QUESTION_FETCH_STARTED });
    let response;
    try {
        response = await AuthRepository.fetchCurrentSecurityQuestion(userId, token);
        if (!response.success) {
            throw new Error("Unexpected result");
        }
    }
    catch (error) {
        return dispatch({
            type: AUTH_ACTIONS.CURRENT_QUESTION_FETCH_FAILED,
            payload: error
        });
    }
    dispatch({
        type: AUTH_ACTIONS.CURRENT_QUESTION_FETCH_SUCCEEDED,
        payload: response.data.question
    });
};
/**
 * Dispatches series of actions while sending logout request to API. Changes state, then redirects to landing page
 *
 * @method logout
 * @returns {Function} Redux thunk
 */
export const logout = () => async (dispatch) => {
    try {
        await AuthRepository.logout();
    }
    catch (error) {
        // ¯\_(ツ)_/¯
    }
    dispatch({
        type: AUTH_ACTIONS.LOGOUT,
        payload: true
    });
    dispatch(pushLocation("/public"));
};
/**
 * Checks the login status if the state is not flagged as loggedIn. Updates state if appropriate.
 *
 * @method checkLoggedInStatus
 * @returns {Function} Redux thunk
 */
export const checkLoggedInStatus = () => async (dispatch, getState) => {
    const state = getState();
    if (state.auth.loggedIn) {
        return;
    }
    if (state.profile.profile == null) {
        // dont try and extend the session here as we need to re-log in
        // to get the profile anyway
        return;
    }
    try {
        await AuthRepository.extendSession();
    }
    catch (error) {
        return;
    }
    dispatch({
        type: AUTH_ACTIONS.LOGIN_SUCCEEDED_TRUE,
        payload: {
            userId: null,
            lastLogin: null
        }
    });
};
export const extendSession = () => async (dispatch) => {
    dispatch(closeSessionModal());
    try {
        await AuthRepository.extendSession();
    }
    catch (error) {
        if (error.response.status === 401) {
            dispatch({ type: AUTH_ACTIONS.LOGOUT, payload: false });
            dispatch(pushLocation("/public"));
        }
        return error;
    }
    dispatch({
        type: AUTH_ACTIONS.SESSION_TIMEOUT_STATUS
    });
};
/**
 * Dispatches series of actions while changing the password
 *
 * @method updatePassword
 * @param {Object} value Value of change password form
 * @returns {Function} Redux thunk
 */
export const updatePassword = (value) => async (dispatch) => {
    dispatch({ type: AUTH_ACTIONS.CHANGE_STARTED });
    const mappedValue = passwordChangeFrontToBack(value);
    let response;
    try {
        response = await AuthRepository.updatePassword(mappedValue);
        if (response.success) {
            apiService.updateXsrfToken(response.data);
        }
    }
    catch (error) {
        dispatch({ type: AUTH_ACTIONS.CHANGE_FAILED, payload: error });
        if (error.response.status === 401) {
            dispatch({ type: AUTH_ACTIONS.LOGOUT, payload: false });
            dispatch(pushLocation("/public"));
        }
        return;
    }
    if (!response.success) {
        const preparedErrors = fieldErrorsBackToFront(response.errors);
        dispatch({ type: AUTH_ACTIONS.CHANGE_SUCCEEDED_FALSE, payload: preparedErrors });
    }
    else {
        dispatch({
            type: AUTH_ACTIONS.CHANGE_SUCCEEDED_TRUE,
            payload: response
        });
        dispatch(pushLocation("/secured/"));
    }
};
/**
 *  Sends email for forgotten password
 *
 * @method forgotPassword
 * @param {String} email Email entered in the form
 * @returns {Function} Redux thunk
 */
export const forgotPassword = (email) => async (dispatch) => {
    dispatch({ type: AUTH_ACTIONS.FORGOT_STARTED });
    const mappedEmail = passwordForgotFrontToBack(email);
    let response;
    try {
        response = await AuthRepository.forgotPassword(mappedEmail);
    }
    catch (error) {
        return dispatch({ type: AUTH_ACTIONS.FORGOT_FAILED, payload: error });
    }
    if (!response.success) {
        const preparedErrors = fieldErrorsBackToFront(response.errors);
        dispatch({ type: AUTH_ACTIONS.FORGOT_SUCCEEDED_FALSE, payload: preparedErrors });
    }
    else {
        dispatch({
            type: AUTH_ACTIONS.FORGOT_SUCCEEDED_TRUE,
            payload: response
        });
    }
};
/**
 * Resets the password
 */
export const resetPassword = (value, userId, token, captchaResponse) => async (dispatch) => {
    dispatch({ type: AUTH_ACTIONS.RESET_STARTED });
    const mappedValue = passwordResetFrontToBack(value, userId, token, captchaResponse);
    let response;
    try {
        response = await AuthRepository.resetPassword(mappedValue);
    }
    catch (error) {
        return dispatch({ type: AUTH_ACTIONS.RESET_FAILED, payload: error });
    }
    if (!response.success) {
        const preparedErrors = fieldErrorsBackToFront(response.errors);
        dispatch({ type: AUTH_ACTIONS.RESET_SUCCEEDED_FALSE, payload: preparedErrors });
    }
    else {
        dispatch({
            type: AUTH_ACTIONS.RESET_SUCCEEDED_TRUE,
            payload: response
        });
        dispatch(pushLocation("/public"));
    }
};
export const apiRequest = (url, authenticated, timestamp, statusId) => ({
    type: AUTH_ACTIONS.API_REQUEST,
    payload: {
        url,
        authenticated,
        timestamp,
        statusId
    }
});
export const showSessionWarning = (timestamp) => {
    return (dispatch) => {
        dispatch({
            type: AUTH_ACTIONS.SESSION_WARNING,
            payload: {
                timestamp: moment(timestamp).toISOString()
            }
        });
        dispatch(openModal({
            modalId: "sessionTimeout",
            triggerId: ""
        }));
    };
};
const closeSessionModal = () => (dispatch, getState) => {
    const { ui } = getState();
    if (ui.isActiveModal && ui.modalId === "sessionTimeout") {
        dispatch(closeModal({
            modalId: "sessionTimeout",
            triggerId: ""
        }));
    }
};
export const sessionExpired = () => {
    return (dispatch) => {
        dispatch(closeSessionModal());
        dispatch(logout());
    };
};



// WEBPACK FOOTER //
// ./src/assets/scripts/domain/auth/authActions.ts